<template>
    <div class="d-flex flex-column dashboard px-4 py-4 w-100">
        <div class="d-flex justify-space-between align-center w-100 my-4">
            <p class="mb-0 custom-title">Manage Pricing</p>
            <v-btn color="primary" @click="openSurchargeDialog">
                <v-icon left size="20" class="mr-2">{{ icons.mdiCogOutline }}</v-icon>
                Surcharge Settings
            </v-btn>
        </div>

        <div class="card-main-box">
            <div class="card-box" v-for="item in priceDetails" :key="item.id">
                <div class="card-body">
                    <div class="left">
                        <span class="price">${{ item.price }}</span>
                    </div>
                    <div class="right ">
                        <span class="hours"> from : {{ item.min_hours }}</span>
                        <span class="hours" style="margin-left: 1.35rem;"> to : {{ item.max_hours }}</span>
                    </div>
                </div>

                <div class="card-footer">
                    <div class="left">
                        <v-btn plain :ripple="false" color="info" @click="openEditDialog(item.id)">
                            <v-icon size="17">{{ icons.mdiPencilOutline }}</v-icon>
                            Edit
                        </v-btn>
                    </div>
                    <div class="right">
                        <v-btn plain :ripple="false" color="primary" @click="openDeleteDialog(item.id)">
                            <v-icon size="17">{{ icons.mdiTrashCanOutline }}</v-icon>
                            Delete
                        </v-btn>
                    </div>
                </div>
            </div>
            <div class="add-price-box">
                <div class="card-body " style="margin-top: -20px;">
                    <button @click.prevent="openSaveDialog">
                        <div class="inner-btn-div">
                            <v-icon size="48">{{ icons.mdiPlusBoxMultipleOutline }}</v-icon>
                            <span class="sub-title">ADD NEW PRICE</span>
                        </div>
                    </button>

                </div>
            </div>
        </div>


        <!-- Save Pricing Dialog -->
        <v-dialog v-model="saveDialog" persistent width="400">
            <v-card>

                <v-card-title class="text-h5">
                    Add Pricing Detail
                </v-card-title>

                <v-card-text style="padding-top: 24px !important;">
                    <v-form validate-on="submit lazy" @submit.prevent="submit">
                        <v-text-field v-model="pricingObj.amount" outlined label="Amount" small></v-text-field>
                        <v-text-field v-model="pricingObj.minHours" outlined label="Minimum Hours"></v-text-field>
                        <v-text-field v-model="pricingObj.maxHours" outlined label="Maximum Hours"></v-text-field>
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text color="green-darken-1" @click="closeSaveDialog">
                        Cancel
                    </v-btn>
                    <v-btn text color="primary" @click="savePrice" :loading="dialogLoading">
                        Save
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Edit Pricing Dialog -->
        <v-dialog v-model="editDialog" persistent width="400">
            <v-card>
                <v-card-title class="text-h5">
                    Edit Pricing Detail
                </v-card-title>
                <v-card-text style="padding-top: 24px !important;">
                    <v-form validate-on="submit lazy" @submit.prevent="submit">
                        <v-text-field v-model="pricingObj.amount" outlined label="Amount" small></v-text-field>
                        <v-text-field v-model="pricingObj.minHours" outlined label="Minimum Hours"></v-text-field>
                        <v-text-field v-model="pricingObj.maxHours" outlined label="Maximum Hours"></v-text-field>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text color="green-darken-1" @click="closeEditDialog">
                        Cancel
                    </v-btn>
                    <v-btn text color="primary" @click="editPrice" :loading="dialogLoading">
                        Save
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete Pricing Confirmation Dialog -->
        <v-dialog v-model="deleteDialog" persistent width="400">
            <v-card>
                <v-card-title>
                    Do you want to delete ?
                </v-card-title>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text color="green-darken-1" @click="closeDeleteDialog">
                        Cancel
                    </v-btn>
                    <v-btn text color="primary" @click="deletePrice" :loading="dialogLoading">
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Surcharge Settings Dialog -->
        <v-dialog v-model="surchargeDialog" persistent width="450">
            <v-card>
                <v-card-title class="text-h5 pb-2">
                    Admin Surcharge Settings
                </v-card-title>
                <v-card-text>
                    <p class="text-subtitle-2 text--secondary mb-4">
                        Configure the surcharge applied to credit card transactions. When active, this percentage will be added to the customer's total bill.
                    </p>
                    <v-form ref="surchargeForm" v-model="isSurchargeValid" validate-on="submit lazy" @submit.prevent="saveSurcharge">
                        <p class="mb-1 text-subtitle-1 font-weight-medium">Apply surcharge on credit card payments:</p>
                        <v-radio-group v-model="surchargeObj.active" row class="mt-0 mb-3">
                            <v-radio label="Yes" :value="true" color="primary"></v-radio>
                            <v-radio label="No" :value="false" color="primary"></v-radio>
                        </v-radio-group>
                        <v-text-field 
                            v-model="surchargeObj.percent" 
                            type="number" 
                            step="0.1" 
                            min="0"
                            max="100"
                            :rules="[
                                v => v !== '' && v !== null && v !== undefined || 'Percentage is required',
                                v => parseFloat(v) >= 0 || 'Must be greater than or equal to 0',
                                v => parseFloat(v) <= 100 || 'Must be less than or equal to 100'
                            ]"
                            outlined 
                            label="Surcharge Percent (%)" 
                            small 
                            class="mt-2"
                        ></v-text-field>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text color="green-darken-1" @click="closeSurchargeDialog">
                        Cancel
                    </v-btn>
                    <v-btn text color="primary" @click="saveSurcharge" :loading="surchargeLoading" :disabled="!isSurchargeValid">
                        Save
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!-- Snackbar for Notifications -->
        <v-snackbar v-model="snackbar" :color="snackbarColor" top right :timeout="3000">
            {{ snackbarText }}
            <template v-slot:action="{ attrs }">
                <v-btn color="white" text v-bind="attrs" @click="snackbar = false">
                    Close
                </v-btn>
            </template>
        </v-snackbar>
    </div>
</template>

<script>
import { mdiPencilOutline, mdiTrashCanOutline, mdiPlusBoxMultipleOutline, mdiCogOutline } from '@mdi/js';
import { ref, onMounted, reactive } from "@vue/composition-api";
import axios from '@axios'
export default {
    setup() {
        
        const PRICING_API_PATH = '/pricing'
        const selectedPriceID = ref('')
        const pricingObj = reactive({
            amount: 0,
            minHours: 0,
            maxHours: 0
        })
        const priceDetails = ref()
        const dialogLoading = ref(false)
        const saveDialog = ref(false)
        const editDialog = ref(false)
        const deleteDialog = ref(false)
        
        const surchargeDialog = ref(false)
        const surchargeLoading = ref(false)
        const surchargeForm = ref(null)
        const isSurchargeValid = ref(true)
        const surchargeObj = reactive({
            active: false,
            percent: 0
        })

        const snackbar = ref(false)
        const snackbarColor = ref('success')
        const snackbarText = ref('')

        // Helper Functions : START

        const startLoading = () => {
            dialogLoading.value = true
        }

        const stopLoading = () => {
            dialogLoading.value = false
        }

        // Helper Function : END


        // Dialog Functions : START

        const openSaveDialog = () => {
            saveDialog.value = true
        }

        const openEditDialog = async (id) => {
            try {
                selectedPriceID.value = id
                const { data:pricing } = await axios.get(`/pricing/${selectedPriceID.value}`)
                if (pricing.data) {
                    pricingObj.amount = pricing.data.price
                    pricingObj.minHours = pricing.data.min_hours
                    pricingObj.maxHours = pricing.data.max_hours
                }
            } catch (error) {
                console.log(error)
            }
            finally {
                editDialog.value = true
            }
        }

        const openDeleteDialog = (id) => {
            deleteDialog.value = true
            selectedPriceID.value = id
        }

        const closeSaveDialog = () => {
            saveDialog.value = false
        }

        const closeEditDialog = () => {
            editDialog.value = false
            selectedPriceID.value = ''
            pricingObj.amount = 0
            pricingObj.minHours = 0
            pricingObj.maxHours = 0
        }

        const closeDeleteDialog = () => {
            deleteDialog.value = false
            selectedPriceID.value = ''
        }

        const openSurchargeDialog = async () => {
            try {
                const { data: response } = await axios.get('/pricing/settings/surcharge')
                if (response.data) {
                    surchargeObj.active = response.data.surcharge_active
                    surchargeObj.percent = response.data.surcharge_percent
                }
            } catch (error) {
                console.log(error)
            } finally {
                surchargeDialog.value = true
            }
        }

        const closeSurchargeDialog = () => {
            surchargeDialog.value = false
        }

        // Dialog Functions : END


        // State Functions : START

        const setPriceDetails = (data) => {
            priceDetails.value = data
        }

        const addPriceDetail = (data) => {
            priceDetails.value.push(data);
        }

        const updatePricing = (data) =>{
            const priceIndex = priceDetails.value.findIndex(price => price.id == data.id)
            if(priceIndex > -1){
                priceDetails.value[priceIndex] = data
            }
        }

        const deletePriceDetail = (priceId) => {
            const priceIndex = priceDetails.value.findIndex(price => price.id == priceId)
            if(priceIndex > -1){
                priceDetails.value.splice(priceIndex, 1)
            }
        }

         // State Functions : END


        // Main Functions : START

        const showSnackbar = (text, color = 'success') => {
            snackbarText.value = text
            snackbarColor.value = color
            snackbar.value = true
        }

        const getPricing = async () => {
            const {data:pricing} = await axios.get(PRICING_API_PATH)
            setPriceDetails(pricing.data)
            return
        }

        const savePrice = async () => {
            try {
                startLoading()
                const {data:pricing} = await axios.post(PRICING_API_PATH, {
                    price: parseFloat(pricingObj.amount),
                    min_hours: parseInt(pricingObj.minHours),
                    max_hours: parseInt(pricingObj.maxHours)
                })
                addPriceDetail(pricing.data)
                closeSaveDialog()
                showSnackbar('Price added successfully')
            } catch (error) {
                const errorMsg = error.response?.data?.message || 'Error adding price'
                showSnackbar(errorMsg, 'error')
                throw error
            }
            finally {
                stopLoading()
                pricingObj.amount = 0
                pricingObj.minHours = 0
                pricingObj.maxHours = 0
                closeSaveDialog()
            }

        }

        const editPrice = async () => {
            try {
                startLoading()
                const {data:pricing} = await axios.patch(`${PRICING_API_PATH}/${selectedPriceID.value}`, {
                    price: parseFloat(pricingObj.amount),
                    min_hours: parseInt(pricingObj.minHours),
                    max_hours: parseInt(pricingObj.maxHours)
                })
                updatePricing(pricing.data)
                showSnackbar('Price updated successfully')
            } catch (error) {
                console.log(error)
                const errorMsg = error.response?.data?.message || 'Error updating price'
                showSnackbar(errorMsg, 'error')
                throw error
            } finally {
                selectedPriceID.value = ''
                stopLoading()
                closeEditDialog()
            }
        }

        const deletePrice = async () => {
            try {
                startLoading()
                await axios.delete(`${PRICING_API_PATH}/${selectedPriceID.value}`)
                deletePriceDetail(selectedPriceID.value)
                showSnackbar('Price deleted successfully')
            } catch (error) {
                console.log(error)
                const errorMsg = error.response?.data?.message || 'Error deleting price'
                showSnackbar(errorMsg, 'error')
                throw error
            }
            finally {
                selectedPriceID.value = ''
                stopLoading()
                closeDeleteDialog()
            }
        }

        const saveSurcharge = async () => {
            if (surchargeForm.value && !surchargeForm.value.validate()) {
                return;
            }

            try {
                surchargeLoading.value = true
                await axios.post('/pricing/settings/surcharge', {
                    surcharge_active: surchargeObj.active,
                    surcharge_percent: parseFloat(surchargeObj.percent)
                })
                closeSurchargeDialog()
                showSnackbar('Surcharge settings saved successfully')
            } catch (error) {
                console.log(error)
                const errorMsg = error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : 'Error saving surcharge settings'
                showSnackbar(errorMsg, 'error')
                throw error
            } finally {
                surchargeLoading.value = false
            }
        }

        // Main Functions : END

        onMounted(() => getPricing())


        return {
            pricingObj,
            savePrice,
            saveDialog,
            openSaveDialog,
            closeSaveDialog,
            editPrice,
            editDialog,
            openEditDialog,
            closeEditDialog,
            deletePrice,
            deleteDialog,
            openDeleteDialog,
            closeDeleteDialog,
            dialogLoading,
            startLoading,
            stopLoading,
            priceDetails,
            surchargeDialog,
            surchargeLoading,
            surchargeForm,
            isSurchargeValid,
            surchargeObj,
            openSurchargeDialog,
            closeSurchargeDialog,
            saveSurcharge,
            snackbar,
            snackbarColor,
            snackbarText,
            icons: {
                mdiPencilOutline,
                mdiTrashCanOutline,
                mdiPlusBoxMultipleOutline,
                mdiCogOutline
            }
        }
    }
}
</script>

<style scoped>
.inner-btn-div {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    transform: translateY(10px);
}

.sub-title {
    font-size: 0.8rem;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    margin-top: 0.3rem;
}

.price {
    font-size: 3.5rem;
    letter-spacing: 1.5px;
    text-transform: uppercase;

}

.hours {
    font-size: 1rem;
    letter-spacing: 1.2px;
    /* text-transform: uppercase; */
}

.card-body {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 80px;
}

.card-footer {
    display: flex;
}

.card-footer .left,
.card-footer .right,
.card-body .left,
.card-body .right {
    width: 50%;
    text-align: center;
}

.right {
    display: flex;
    flex-direction: column;
}

.card-main-box {
    width: 100%;
    /* display: flex;
    flex-wrap: wrap;
    gap: 1rem; */
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
}

.card-box {
    background-color: #fff;
    width: 100%;
    max-width: 100%;
    /* padding: 20px 30px 10px 30px !important; */
    border-radius: 8px;
    height: 140px;
    padding: 10px;
}

.add-price-box {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #fff;
    width: 100%;
    max-width: 100%;
    border-radius: 8px;
    height: 140px;
    padding: 10px;
}

.box-title,
.card-heading,
.custom-title {
    font-size: 0.9rem;
    letter-spacing: 2px;
    text-align: left;
    text-transform: uppercase;
}

.custom-title {
    font-size: 2rem;
    letter-spacing: 1.5px;
}


@media only screen and (max-width: 576px) {
    .card-main-box {
        grid-template-columns: repeat(1, 1fr) !important;
    }
}

@media only screen and (max-width: 992px) {
    .card-main-box {
        grid-template-columns: repeat(2, 1fr);
    }
}
</style>